/**
 * @fileoverview "New Prospect" (Create Prospect) form for the Sales Pipeline page.
 * @description Creates a prospect through the backend's Create Prospect (POST /sales/prospects,
 * Confluence 3.5.11.1), in the five steps the design specifies — Basic Info, Address,
 * Organisation, Primary Contact, Sales Info — saved together at the final step. Every choice comes
 * from the backend's prospect metadata (GET /tenants/prospect-metadata): bank types, states, core
 * banking solutions, lead sources and the tier scheme the read-only Bank Tier preview is derived
 * from. Who is creating it decides the rest, and the backend applies it: a Super Admin, Sales Head
 * or Sales Manager may name an eligible Sales Executive as owner (GET
 * /sales/prospects/eligible-owners) or leave it unassigned, and the prospect joins the backlog at
 * once; a Sales Executive's prospect is submitted for review instead, with them as its proposed
 * owner. One idempotency key is kept per submission, so retrying after a failure never creates a
 * second prospect. Step navigation is click-to-jump on the step indicator itself (any already-visited
 * step, not forward ones — those still require "Next" to validate into).
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 * @author Anshul Rokde
 */

import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { useCreateProspect } from '@/features/sales/hooks/useCreateProspect'
import { useEligibleProspectOwners } from '@/features/sales/hooks/useEligibleProspectOwners'
import { useProspectMetadata } from '@/features/sales/hooks/useProspectMetadata'
import {
  absoluteWebsite,
  INDIA_COUNTRY_CODE,
  tierNameFor,
} from '@/features/sales/lib/prospect-form.utils'
import {
  createProspectFormSchema,
  type CreateProspectFormValues,
} from '@/features/sales/schemas/create-prospect-form.schema'
import { newIdempotencyKey } from '@/features/sales/services/prospects.service'
import ModalFooterButton from '@/shared/components/atoms/ModalFooterButton'
import FormField from '@/shared/components/molecules/FormField'
import Modal from '@/shared/components/molecules/Modal'
import SelectField from '@/shared/components/molecules/SelectField'
import { MOBILE_NUMBER_INPUT_PROPS } from '@/shared/lib/validation/contact-fields.constants'

import SalesRequestError from './SalesRequestError'

import type { CreateProspectRequest, ProspectCreated } from '@/features/sales/types/prospect.types'
import type { FormEvent } from 'react'

interface CreateProspectFormProps {
  open: boolean
  onClose: () => void
  actingUserRole: string
  canAssignOwner?: boolean
  /** Called with the saved prospect and its bank name, before the form closes — e.g. to say it
   * awaits approval. */
  onCreated?: (created: ProspectCreated, bankName: string) => void
}

type Step = 1 | 2 | 3 | 4 | 5

const TOTAL_STEPS = 5

const STEP_FIELDS: Record<Step, (keyof CreateProspectFormValues)[]> = {
  1: ['name', 'bankTypeId', 'headOffice', 'regNumber', 'website'],
  2: ['stateId', 'city', 'district', 'pincode', 'address'],
  3: ['branches', 'employees', 'coreBankingSolutionId', 'existingMis'],
  4: ['contactName', 'contactDesignation', 'contactEmail', 'contactPhone'],
  5: ['leadSourceId', 'leadRating', 'notes', 'proposedOwnerUserId'],
}

const DEFAULT_VALUES: CreateProspectFormValues = {
  name: '',
  bankTypeId: '',
  headOffice: '',
  regNumber: '',
  website: '',
  stateId: '',
  city: '',
  district: '',
  pincode: '',
  address: '',
  branches: 1,
  employees: undefined,
  coreBankingSolutionId: '',
  existingMis: '',
  contactName: '',
  contactDesignation: '',
  contactEmail: '',
  contactPhone: '',
  leadSourceId: '',
  leadRating: 'WARM',
  notes: '',
  proposedOwnerUserId: '',
}

/**
 * @description Appends a required-field marker to a label — every field validated as mandatory by
 * createProspectFormSchema gets one, so the user can tell at a glance which fields block "Next".
 * @param {string} label - The field's translated label.
 * @returns {string} The label with a trailing " *".
 */
function req(label: string): string {
  return `${label} *`
}

/**
 * @description Turns the validated form into Create Prospect's request body.
 * @param {CreateProspectFormValues} values - The validated form values.
 * @param {boolean} mayAssign - Whether the caller may name an owner.
 * @returns {CreateProspectRequest} The request body.
 */
function toCreateProspectRequest(
  values: CreateProspectFormValues,
  mayAssign: boolean,
): CreateProspectRequest {
  return {
    bank: {
      name: values.name,
      bankTypeId: values.bankTypeId,
      registrationNumber: values.regNumber,
      headOfficeLocation: values.headOffice,
      website: absoluteWebsite(values.website),
    },
    address: {
      streetAddress: values.address,
      stateId: values.stateId,
      city: values.city,
      district: values.district,
      pinCode: values.pincode,
    },
    organisation: {
      branchCount: values.branches,
      employeeCount: values.employees ?? null,
      coreBankingSolutionId: values.coreBankingSolutionId,
      existingMisSolution: values.existingMis || null,
    },
    primaryContact: {
      name: values.contactName,
      designation: values.contactDesignation || null,
      email: values.contactEmail,
      countryCode: INDIA_COUNTRY_CODE,
      mobileNumber: values.contactPhone,
    },
    sales: {
      leadSourceId: values.leadSourceId,
      leadRating: values.leadRating,
      notes: values.notes || null,
      proposedOwnerUserId: mayAssign ? values.proposedOwnerUserId || null : null,
    },
  }
}

interface StepProgressProps {
  step: Step
  labels: [string, string, string, string, string]
  onStepClick: (step: Step) => void
}

/**
 * @description A 5-step indicator with connecting lines — a done step shows a checkmark in a pale
 * circle and is clickable to jump back to it; the current step is a solid filled circle; steps not
 * yet reached stay inert (grey, non-clickable) since their own fields haven't been validated into
 * yet. Only the active step's label is bold/colored — both done and pending labels stay muted, so
 * the current step is the one thing that visually stands out. Each step's circle and label are
 * grouped into their own non-growing column (not the connecting line that follows it), so the label
 * sits centered directly under its own circle rather than under the wider cell that line would
 * otherwise stretch it into. Below the sm breakpoint, only the active step's own label stays
 * visible — the other 4 hide, so a cramped phone width shows the compact circle/line trail with
 * just the one label that matters, rather than all 5 crowding together or disappearing entirely.
 * @param {StepProgressProps} props - The current step, each step's label, and the jump handler.
 */
function StepProgress({ step, labels, onStepClick }: StepProgressProps) {
  return (
    <div className="flex items-start" data-testid="create-prospect-steps">
      {labels.map((label, index) => {
        const stepNumber = (index + 1) as Step
        const isDone = stepNumber < step
        const isActive = stepNumber === step
        const isReached = stepNumber <= step
        const circle = (
          <span
            className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-bold sm:h-7 sm:w-7 sm:text-[12px] ${
              isActive
                ? 'bg-primary text-white'
                : isDone
                  ? 'border border-primary bg-primary/10 text-primary'
                  : 'border border-input-border text-slate'
            }`}
          >
            {isDone ? '✓' : stepNumber}
          </span>
        )
        return (
          // `display: contents` so the column and its trailing connector line are each direct flex
          // items of the row above, instead of both being nested inside one flex-1 cell (which is
          // what pushed the label out from under its circle in the first place).
          <div key={label} className="contents">
            <div className="flex flex-col items-center gap-1.5 px-0.5">
              {isReached ? (
                <button
                  type="button"
                  onClick={() => onStepClick(stepNumber)}
                  className="flex shrink-0 cursor-pointer items-center"
                  aria-label={label}
                  data-testid={`create-prospect-step-${stepNumber}-button`}
                  aria-current={isActive ? 'step' : undefined}
                >
                  {circle}
                </button>
              ) : (
                circle
              )}
              <span
                data-testid={`create-prospect-step-${stepNumber}-label`}
                className={`whitespace-nowrap text-[11.5px] font-semibold ${
                  isActive ? 'block text-primary' : 'hidden text-slate sm:block'
                }`}
              >
                {label}
              </span>
            </div>
            {stepNumber < labels.length && (
              <div
                className={`mt-3 h-px flex-1 sm:mt-3.5 ${isDone ? 'bg-primary' : 'bg-divider'}`}
              />
            )}
          </div>
        )
      })}
    </div>
  )
}

/**
 * @component
 * @param {CreateProspectFormProps} props - Whether the dialog is open, its close handler, and the
 * signed-in user's identity.
 * @param {boolean} props.open - Whether the dialog is visible.
 * @param {() => void} props.onClose - Called after a successful submit or an explicit cancel.
 * @param {string} props.actingUserRole - The signed-in user's active role name: a Sales Executive's
 * prospect is submitted for review.
 * @param {boolean} [props.canAssignOwner] - Whether the caller holds SALES_CRM.PROSPECT.ASSIGN, which
 * shows the owner selector; defaults to any non-Executive path.
 * @param {(created: ProspectCreated, bankName: string) => void} [props.onCreated] - Called with the
 * saved prospect.
 */
function CreateProspectForm({
  open,
  onClose,
  actingUserRole,
  canAssignOwner,
  onCreated,
}: CreateProspectFormProps) {
  const { t } = useTranslation()
  const createProspect = useCreateProspect()
  const isExecutive = actingUserRole === 'Sales Executive'
  const showsOwnerSelector = !isExecutive && (canAssignOwner ?? true)
  const metadataQuery = useProspectMetadata()
  const ownersQuery = useEligibleProspectOwners(open && showsOwnerSelector)
  const metadata = metadataQuery.data
  const owners = ownersQuery.data ?? []
  // One key per submission: a retry after a failure resends it, so the backend never creates the
  // prospect twice; a fresh one is drawn whenever the form is reset.
  const [idempotencyKey, setIdempotencyKey] = useState(newIdempotencyKey)
  const {
    register,
    handleSubmit,
    trigger,
    reset,
    control,
    formState: { errors },
  } = useForm<CreateProspectFormValues>({
    resolver: zodResolver(createProspectFormSchema),
    defaultValues: DEFAULT_VALUES,
  })

  const [step, setStep] = useState<Step>(1)
  const [visitedStep, setVisitedStep] = useState<Step>(1)

  const watchedBranches = useWatch({ control, name: 'branches' })
  const previewTier = tierNameFor(metadata?.tierScheme.ranges ?? [], watchedBranches || 0)

  /**
   * @description Resets the form and step back to the start, then closes the dialog — used by both
   * a successful submit and an explicit cancel, so reopening always starts clean. Also resets both
   * mutation's own error state and draws a new idempotency key: this component is always mounted by
   * the page (only the Modal's own `open` prop toggles visibility), so a failed create would
   * otherwise still be showing its stale error banner the next time this wizard is reopened. See
   * `.claude/rules/react.md`'s "Modal and dialog lifecycle" section.
   */
  const resetAndClose = () => {
    reset(DEFAULT_VALUES)
    setStep(1)
    setVisitedStep(1)
    createProspect.reset()
    setIdempotencyKey(newIdempotencyKey())
    onClose()
  }

  /**
   * @description Jumps directly to an already-visited step — the step indicator's own click
   * handler, replacing a separate Back button. Steps beyond the furthest one reached are ignored,
   * since their fields haven't been validated into yet.
   * @param {Step} target - The step to jump to.
   */
  const jumpToStep = (target: Step) => {
    if (target <= visitedStep) {
      setStep(target)
    }
  }

  /**
   * @description Creates the prospect; the backend decides whether it needs review.
   * @param {CreateProspectFormValues} values - The validated form values.
   */
  const onValid = (values: CreateProspectFormValues) => {
    createProspect.mutate(
      { request: toCreateProspectRequest(values, showsOwnerSelector), idempotencyKey },
      {
        onSuccess: (created) => {
          onCreated?.(created, values.name)
          resetAndClose()
        },
      },
    )
  }

  /**
   * @description Validates only the current step's own fields and, if valid, advances to the next
   * one — never submits the mutation early, since later steps may still have required fields.
   */
  const goToNextStep = async () => {
    const isStepValid = await trigger(STEP_FIELDS[step])
    if (isStepValid && step < TOTAL_STEPS) {
      const next = (step + 1) as Step
      setStep(next)
      setVisitedStep((current) => (next > current ? next : current))
    }
  }

  /**
   * @description On the final step, runs full-schema validation and submits; on an earlier step,
   * advances instead — so pressing Enter anywhere in the form does the right thing for that step.
   * @param {FormEvent<HTMLFormElement>} event - The form submit event.
   */
  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (step === TOTAL_STEPS) {
      void handleSubmit(onValid)(event)
    } else {
      void goToNextStep()
    }
  }

  const stepLabels: [string, string, string, string, string] = [
    t('sales.pipeline.create.steps.basicInfo'),
    t('sales.pipeline.create.steps.address'),
    t('sales.pipeline.create.steps.organisation'),
    t('sales.pipeline.create.steps.primaryContact'),
    t('sales.pipeline.create.steps.salesInfo'),
  ]
  const selectPlaceholder = t('sales.pipeline.form.selectPlaceholder')
  const isSubmitting = createProspect.isPending
  const bankTypeOptions = (metadata?.bankTypes ?? []).map((entry) => ({
    value: entry.id,
    label: entry.name,
  }))
  const toOptions = (entries: { id: string; name: string }[]) => [
    { value: '', label: selectPlaceholder },
    ...entries.map((entry) => ({ value: entry.id, label: entry.name })),
  ]

  return (
    <Modal
      open={open}
      onClose={resetAndClose}
      data-testid="create-prospect-modal"
      title={t('sales.pipeline.create.title')}
      subtitle={t('sales.pipeline.create.steps.stepHeader', {
        current: step,
        total: TOTAL_STEPS,
        stepName: stepLabels[step - 1],
      })}
      size="lg"
      footer={
        <div className="flex w-full flex-wrap items-center justify-between gap-2.5">
          <ModalFooterButton
            type="button"
            onClick={resetAndClose}
            data-testid="create-prospect-cancel-button"
          >
            {t('sales.pipeline.form.cancel')}
          </ModalFooterButton>
          <span
            className="order-last hidden text-[12.5px] text-slate sm:order-none sm:block"
            data-testid="create-prospect-footer-hint"
          >
            {t('sales.pipeline.create.footerHint')}
          </span>
          <div className="flex items-center gap-2.5">
            {step > 1 && (
              <ModalFooterButton
                type="button"
                onClick={() => setStep((current) => (current - 1) as Step)}
                data-testid="create-prospect-back-button"
              >
                {t('sales.pipeline.create.back')}
              </ModalFooterButton>
            )}
            {step < TOTAL_STEPS ? (
              <ModalFooterButton
                type="submit"
                variant="primary"
                form="create-prospect-form"
                data-testid="create-prospect-next-button"
              >
                {t('sales.pipeline.create.next')}
              </ModalFooterButton>
            ) : (
              <ModalFooterButton
                type="submit"
                variant="primary"
                form="create-prospect-form"
                isLoading={isSubmitting}
                data-testid="create-prospect-submit-button"
              >
                {t('sales.pipeline.create.submit')}
              </ModalFooterButton>
            )}
          </div>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <StepProgress step={step} labels={stepLabels} onStepClick={jumpToStep} />
        <div className="-mx-6 border-b border-divider" />

        <form
          id="create-prospect-form"
          data-testid="create-prospect-form"
          onSubmit={onSubmit}
          noValidate
          className="grid grid-cols-1 gap-4 sm:grid-cols-2"
        >
          {step === 1 && (
            <>
              <div className="col-span-full">
                <FormField
                  id="createName"
                  data-testid="create-prospect-name-input"
                  size="md"
                  placeholder={t('sales.pipeline.create.fields.namePlaceholder')}
                  label={req(t('sales.pipeline.create.fields.name'))}
                  error={errors.name && t(errors.name.message ?? '')}
                  {...register('name')}
                />
              </div>
              <SelectField
                id="createBankType"
                data-testid="create-prospect-bank-type-select"
                label={req(t('sales.pipeline.create.fields.bankType'))}
                options={[{ value: '', label: selectPlaceholder }, ...bankTypeOptions]}
                error={errors.bankTypeId && t(errors.bankTypeId.message ?? '')}
                {...register('bankTypeId')}
              />
              <FormField
                id="createRegNumber"
                data-testid="create-prospect-reg-number-input"
                size="md"
                placeholder={t('sales.pipeline.create.fields.regNumberPlaceholder')}
                label={req(t('sales.pipeline.create.fields.regNumber'))}
                error={errors.regNumber && t(errors.regNumber.message ?? '')}
                {...register('regNumber')}
              />
              <FormField
                id="createHeadOffice"
                data-testid="create-prospect-head-office-input"
                size="md"
                placeholder={t('sales.pipeline.create.fields.headOfficePlaceholder')}
                label={req(t('sales.pipeline.create.fields.headOffice'))}
                error={errors.headOffice && t(errors.headOffice.message ?? '')}
                {...register('headOffice')}
              />
              <FormField
                id="createWebsite"
                data-testid="create-prospect-website-input"
                size="md"
                placeholder={t('sales.pipeline.create.fields.websitePlaceholder')}
                label={t('sales.pipeline.create.fields.website')}
                {...register('website')}
              />
            </>
          )}

          {step === 2 && (
            <>
              <div className="col-span-full">
                <FormField
                  id="createAddress"
                  data-testid="create-prospect-address-input"
                  size="md"
                  placeholder={t('sales.pipeline.create.fields.addressPlaceholder')}
                  label={req(t('sales.pipeline.create.fields.address'))}
                  error={errors.address && t(errors.address.message ?? '')}
                  {...register('address')}
                />
              </div>
              <SelectField
                id="createState"
                data-testid="create-prospect-state-select"
                label={req(t('sales.pipeline.create.fields.state'))}
                options={toOptions(metadata?.states ?? [])}
                error={errors.stateId && t(errors.stateId.message ?? '')}
                {...register('stateId')}
              />
              <FormField
                id="createCity"
                data-testid="create-prospect-city-input"
                size="md"
                placeholder={t('sales.pipeline.create.fields.cityPlaceholder')}
                label={req(t('sales.pipeline.create.fields.city'))}
                error={errors.city && t(errors.city.message ?? '')}
                {...register('city')}
              />
              <FormField
                id="createDistrict"
                data-testid="create-prospect-district-input"
                size="md"
                placeholder={t('sales.pipeline.create.fields.districtPlaceholder')}
                label={req(t('sales.pipeline.create.fields.district'))}
                error={errors.district && t(errors.district.message ?? '')}
                {...register('district')}
              />
              <FormField
                id="createPincode"
                data-testid="create-prospect-pincode-input"
                size="md"
                placeholder={t('sales.pipeline.create.fields.pincodePlaceholder')}
                label={req(t('sales.pipeline.create.fields.pincode'))}
                error={errors.pincode && t(errors.pincode.message ?? '')}
                inputMode="numeric"
                maxLength={6}
                {...register('pincode')}
              />
            </>
          )}

          {step === 3 && (
            <>
              <FormField
                id="createBranches"
                data-testid="create-prospect-branches-input"
                size="md"
                type="number"
                placeholder={t('sales.pipeline.create.fields.branchesPlaceholder')}
                label={req(t('sales.pipeline.create.fields.branches'))}
                error={errors.branches && t(errors.branches.message ?? '')}
                {...register('branches', { valueAsNumber: true })}
              />
              <FormField
                id="createEmployees"
                data-testid="create-prospect-employees-input"
                size="md"
                type="number"
                placeholder={t('sales.pipeline.create.fields.employeesPlaceholder')}
                label={t('sales.pipeline.create.fields.employees')}
                {...register('employees', {
                  setValueAs: (value) => (value === '' ? undefined : Number(value)),
                })}
              />
              <div>
                <SelectField
                  id="createBankTier"
                  data-testid="create-prospect-bank-tier-select"
                  label={t('sales.pipeline.create.fields.bankTier')}
                  disabled
                  value={previewTier}
                  onChange={() => {}}
                  options={[{ value: previewTier, label: previewTier }]}
                />
                <p
                  className="mt-1.5 text-[12px] text-slate"
                  data-testid="create-prospect-bank-tier-hint"
                >
                  {t('sales.pipeline.create.fields.bankTierHint')}
                </p>
              </div>
              <SelectField
                id="createCoreBanking"
                data-testid="create-prospect-core-banking-select"
                label={req(t('sales.pipeline.create.fields.coreBanking'))}
                error={
                  errors.coreBankingSolutionId && t(errors.coreBankingSolutionId.message ?? '')
                }
                options={toOptions(metadata?.coreBankingSolutions ?? [])}
                {...register('coreBankingSolutionId')}
              />
              <FormField
                id="createExistingMis"
                data-testid="create-prospect-existing-mis-input"
                size="md"
                label={t('sales.pipeline.create.fields.existingMis')}
                {...register('existingMis')}
              />
            </>
          )}

          {step === 4 && (
            <>
              <FormField
                id="createContactName"
                data-testid="create-prospect-contact-name-input"
                size="md"
                placeholder={t('sales.pipeline.create.fields.contactNamePlaceholder')}
                label={req(t('sales.pipeline.create.fields.contactName'))}
                error={errors.contactName && t(errors.contactName.message ?? '')}
                {...register('contactName')}
              />
              <FormField
                id="createContactDesignation"
                data-testid="create-prospect-contact-designation-input"
                size="md"
                placeholder={t('sales.pipeline.create.fields.contactDesignationPlaceholder')}
                label={t('sales.pipeline.create.fields.contactDesignation')}
                {...register('contactDesignation')}
              />
              <FormField
                id="createContactEmail"
                data-testid="create-prospect-contact-email-input"
                size="md"
                type="email"
                placeholder={t('sales.pipeline.create.fields.contactEmailPlaceholder')}
                label={req(t('sales.pipeline.create.fields.contactEmail'))}
                error={errors.contactEmail && t(errors.contactEmail.message ?? '')}
                {...register('contactEmail')}
              />
              <FormField
                id="createContactPhone"
                data-testid="create-prospect-contact-phone-input"
                size="md"
                placeholder={t('sales.pipeline.create.fields.contactPhonePlaceholder')}
                label={req(t('sales.pipeline.create.fields.contactPhone'))}
                error={errors.contactPhone && t(errors.contactPhone.message ?? '')}
                {...MOBILE_NUMBER_INPUT_PROPS}
                {...register('contactPhone')}
              />
            </>
          )}

          {step === 5 && (
            <>
              <SelectField
                id="createLeadSource"
                data-testid="create-prospect-lead-source-select"
                label={req(t('sales.pipeline.create.fields.leadSource'))}
                options={toOptions(metadata?.leadSources ?? [])}
                error={errors.leadSourceId && t(errors.leadSourceId.message ?? '')}
                {...register('leadSourceId')}
              />
              <SelectField
                id="createLeadRating"
                data-testid="create-prospect-lead-rating-select"
                label={req(t('sales.pipeline.create.fields.leadRating'))}
                options={(metadata?.leadRatings ?? ['HOT', 'WARM', 'COLD']).map((rating) => ({
                  value: rating,
                  label: t(`sales.pipeline.ratings.${rating}`),
                }))}
                error={errors.leadRating && t(errors.leadRating.message ?? '')}
                {...register('leadRating')}
              />
              <div className="col-span-full">
                <FormField
                  id="createNotes"
                  data-testid="create-prospect-notes-input"
                  size="md"
                  label={t('sales.pipeline.create.fields.notes')}
                  {...register('notes')}
                />
              </div>
              <div className="col-span-full">
                {!showsOwnerSelector ? (
                  <FormField
                    id="createExecutive"
                    data-testid="create-prospect-executive-input"
                    size="md"
                    label={t('sales.pipeline.create.fields.executive')}
                    value={
                      isExecutive
                        ? t('sales.pipeline.create.fields.selfAssignee')
                        : t('sales.pipeline.filters.unassigned')
                    }
                    readOnly
                  />
                ) : (
                  <>
                    <SelectField
                      id="createExecutive"
                      data-testid="create-prospect-executive-select"
                      label={t('sales.pipeline.create.fields.executive')}
                      disabled={owners.length === 0}
                      options={[
                        { value: '', label: t('sales.pipeline.filters.unassigned') },
                        ...owners.map((owner) => ({
                          value: owner.userId,
                          label: owner.employeeCode
                            ? `${owner.displayName} (${owner.employeeCode})`
                            : owner.displayName,
                        })),
                      ]}
                      {...register('proposedOwnerUserId')}
                    />
                    {ownersQuery.isSuccess && owners.length === 0 && (
                      <p
                        className="mt-1.5 text-[12px] text-slate"
                        data-testid="create-prospect-no-executives-message"
                      >
                        {t('sales.pipeline.create.fields.executiveNone')}
                      </p>
                    )}
                    {ownersQuery.isError && (
                      <div className="mt-1.5">
                        <SalesRequestError error={ownersQuery.error} />
                      </div>
                    )}
                  </>
                )}
                {isExecutive && (
                  <p
                    className="mt-1.5 text-[12px] font-medium text-status-warning"
                    data-testid="create-prospect-approval-note"
                  >
                    {t('sales.pipeline.create.approvalNoteRouted')}
                  </p>
                )}
              </div>
            </>
          )}

          {metadataQuery.isError && (
            <p
              role="alert"
              className="col-span-full text-[13px] text-danger"
              data-testid="create-prospect-metadata-error"
            >
              {t('sales.pipeline.create.metadataError')}
            </p>
          )}
          {createProspect.isError && (
            <p
              role="alert"
              className="col-span-full text-[13px] text-danger"
              data-testid="create-prospect-error"
            >
              {createProspect.error.message}
            </p>
          )}
        </form>
      </div>
    </Modal>
  )
}

export default CreateProspectForm
